import { useMutation, useQuery } from "@tanstack/react-query";
import axiosInstance from "./apiConfig";
export const apiGet = async (url: string, params = {}, direct?: boolean) => {
  try {
    let response = await axiosInstance.get(url, { params });
    return { data: response.data, error: null }; // Return both data and error for consistency
  } catch (error: any) {
    return { data: null, error: error.response?.data || error.message };
  }
};

// Generic function for POST requests
export const apiPost = async (url: string, data = {}, direct?: boolean) => {
  try {
    let response = await axiosInstance.post(url, data);
    return {
      data: response.data,
      error: null,
      status: response.request?.status,
    };
  } catch (error: any) {
    return {
      data: null,
      error: error.response?.data || error.message,
      status: error.request?.status,
    };
  }
};

// Generic function for PUT requests
export const apiPut = async (url: string, data = {}) => {
  try {
    const response = await axiosInstance.put(url, data);
    return { data: response.data, error: null };
  } catch (error: any) {
    return { data: null, error: error.response?.data || error.message };
  }
};

export const apiPatch = async (url: string, data = {}) => {
  try {
    const response = await axiosInstance.patch(url, data);
    return { data: response.data, error: null };
  } catch (error: any) {
    return { data: null, error: error.response?.data || error.message };
  }
};

// Generic function for DELETE requests
export const apiDelete = async (url: string, params = {}) => {
  try {
    const response = await axiosInstance.delete(url, { params });
    return { data: response.data, error: null };
  } catch (error: any) {
    return { data: null, error: error.response?.data || error.message };
  }
};

// react query configs
const createGET = async (url: string, params = {}) => {
  const response = await axiosInstance.get(url, { params });
  return response.data;
};

export const useAxiosRM = (
  url: string,
  params = {},
  onSuccess?: Function,
  onMutate?: Function,
  onError?: Function
) => {
  return useMutation({
    mutationFn: () => createGET(url, params),
    onSuccess(data: any, variables, context) {
      onSuccess && onSuccess(data);
    },
    onMutate() {
      onMutate && onMutate();
    },
    onError() {
      onError && onError();
    },
  });
};

export const useAxiosRQ = (
  url: string,
  queryKey: string,
  params = {},
  options = {}
) => {
  return useQuery({
    queryKey: [queryKey, params],
    queryFn: () => createGET(url, params),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep data in cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch when component remounts
    refetchOnReconnect: false, // Don't refetch when reconnecting to network
    ...options, // Allow overriding default options
  });
};
